import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { genres } from "@/mocks/comics";

const navLinks = [
  { to: "/", label: "首页" },
  { to: "/category/全部", label: "分类" },
  { to: "/search", label: "搜索" },
  { to: "/about", label: "关于" },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  const isActive = (to: string) => {
    if (to === "/") return location.pathname === "/";
    return location.pathname.startsWith(to.split("/").slice(0, 2).join("/"));
  };

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = keyword.trim();
    if (trimmed) {
      navigate(`/search?q=${encodeURIComponent(trimmed)}`);
      setMobileOpen(false);
      setKeyword("");
    }
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex h-16 items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-book-open-line text-lg"></i>
            </span>
            <span className="font-heading text-xl text-foreground-950 whitespace-nowrap">
              歪歪漫画
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <div key={link.to} className="relative">
                {link.label === "分类" ? (
                  <div
                    onMouseEnter={() => setCategoryOpen(true)}
                    onMouseLeave={() => setCategoryOpen(false)}
                  >
                    <Link
                      to={link.to}
                      className={`px-3 py-2 text-sm rounded-md transition-colors whitespace-nowrap flex items-center gap-1 ${
                        isActive(link.to)
                          ? "text-primary-600 font-medium"
                          : "text-foreground-700 hover:text-primary-600"
                      }`}
                    >
                      分类
                      <i className="ri-arrow-down-s-line text-base"></i>
                    </Link>
                    {categoryOpen && (
                      <div className="absolute left-0 top-full w-[420px] rounded-lg border border-background-200 bg-background-50 p-4 grid grid-cols-4 gap-2 animate-fade-in">
                        {genres.map((g) => (
                          <Link
                            key={g}
                            to={`/category/${encodeURIComponent(g)}`}
                            className="rounded-md px-2 py-1.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 text-center transition-colors"
                            onClick={() => setCategoryOpen(false)}
                          >
                            {g}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={link.to}
                    className={`px-3 py-2 text-sm rounded-md transition-colors whitespace-nowrap ${
                      isActive(link.to)
                        ? "text-primary-600 font-medium"
                        : "text-foreground-700 hover:text-primary-600"
                    }`}
                  >
                    {link.label}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          <form
            onSubmit={submitSearch}
            className="hidden lg:flex items-center gap-2 rounded-full bg-background-100 px-3 py-1.5 border border-background-200"
          >
            <i className="ri-search-line text-foreground-500 text-base"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索漫画标题"
              className="w-40 xl:w-56 bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
            />
            <button
              type="submit"
              className="rounded-full bg-primary-500 px-3 py-1 text-xs text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              搜索
            </button>
          </form>

          <button
            type="button"
            aria-label="打开菜单"
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden flex h-10 w-10 items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 cursor-pointer"
          >
            <i className={mobileOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 py-4 animate-fade-in">
          <form onSubmit={submitSearch} className="mb-3 flex items-center gap-2 rounded-full bg-background-100 px-3 py-2 border border-background-200">
            <i className="ri-search-line text-foreground-500"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索漫画标题"
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-foreground-400"
            />
          </form>
          <div className="flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileOpen(false)}
                className="rounded-md px-3 py-2.5 text-sm text-foreground-800 hover:bg-primary-50 hover:text-primary-600"
              >
                {link.label}
              </Link>
            ))}
          </div>
          <div className="mt-3 border-t border-background-200 pt-3">
            <p className="mb-2 text-xs text-foreground-500">热门分类</p>
            <div className="flex flex-wrap gap-2">
              {genres.slice(0, 8).map((g) => (
                <Link
                  key={g}
                  to={`/category/${encodeURIComponent(g)}`}
                  onClick={() => setMobileOpen(false)}
                  className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-700"
                >
                  {g}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}